import type { PortRect } from '../types';
import { POE_STANDARD_LABEL, type PoEInSpec, type PoEOutSpec } from '../types/deviceModel';
import { portKindDisplayName } from './customPortTypes';

export type PoeRj45Look = 'data' | 'poe-in' | 'poe-out' | 'poe-both';

const RJ45_GREEN = '#10b981';
const POE_OUT_YELLOW = '#eab308';
const POE_IN_PINK = '#ec4899';

export function poeRj45Look(port: {
  kind?: string | null;
  poeIn?: unknown;
  poeOut?: unknown;
}): PoeRj45Look {
  if (String(port.kind || '') !== 'rj45') return 'data';
  const hasIn = !!port.poeIn;
  const hasOut = !!port.poeOut;
  if (hasIn && hasOut) return 'poe-both';
  if (hasIn) return 'poe-in';
  if (hasOut) return 'poe-out';
  return 'data';
}

export function poeRj45Border(look: PoeRj45Look): string {
  if (look === 'poe-out' || look === 'poe-both') return POE_OUT_YELLOW;
  if (look === 'poe-in') return POE_IN_PINK;
  return RJ45_GREEN;
}

export function poeRj45Fill(): string {
  return RJ45_GREEN;
}

export function poeRj45Caption(look: PoeRj45Look): string {
  if (look === 'poe-out') return 'PoE-out';
  if (look === 'poe-in') return 'PoE-in';
  if (look === 'poe-both') return 'PoE-in + PoE-out';
  return '';
}

export function poeRj45Title(base: string, port: Pick<PortRect, 'kind' | 'poeIn' | 'poeOut'>): string {
  const look = poeRj45Look(port);
  const cap = poeRj45Caption(look);
  return cap ? `${base} · ${cap}` : base;
}

export const POE_RJ45_COLORS = {
  green: RJ45_GREEN,
  out: POE_OUT_YELLOW,
  in: POE_IN_PINK,
};

/** Mix a hex color toward black. Non-hex values are returned unchanged. */
export function shadeHex(hex: string, factor: number): string {
  const raw = hex.trim();
  const m = raw.match(/^#?([0-9a-f]{6})$/i);
  if (!m) return hex;
  const n = m[1];
  const f = Math.min(1, Math.max(0, factor));
  const ch = (i: number) => {
    const v = Math.round(parseInt(n.slice(i, i + 2), 16) * f);
    return v.toString(16).padStart(2, '0');
  };
  return `#${ch(0)}${ch(2)}${ch(4)}`;
}

export function portHoverTooltip(port: PortRect, connected: boolean): { title: string; lines: string[] } {
  const name = (port.label || `Port ${port.number}`).trim();
  const kind = portKindDisplayName(port.kind);
  const lines: string[] = [kind];
  if (port.speedsMbps && port.speedsMbps.length > 0) {
    const speeds = [...port.speedsMbps].sort((a, b) => a - b)
      .map((mbps) => (mbps >= 1000 ? `${mbps / 1000}G` : `${mbps}M`))
      .join(' / ');
    lines.push(speeds);
  }
  const look = poeRj45Look(port);
  if (look !== 'data') lines.push(poeRj45Caption(look));
  const poeIn = port.poeIn as PoEInSpec | undefined;
  if (poeIn?.standards?.length) {
    const std = poeIn.standards.map((s) => POE_STANDARD_LABEL[s] || s).join(', ');
    const watts = poeIn.maxWatts != null ? ` · ${poeIn.maxWatts}W` : '';
    lines.push(`In ${std}${watts}`);
  }
  const poeOut = port.poeOut as PoEOutSpec | undefined;
  if (poeOut?.standards?.length) {
    const std = poeOut.standards.map((s) => POE_STANDARD_LABEL[s] || s).join(', ');
    const watts = poeOut.perPortMaxWatts != null ? ` · ${poeOut.perPortMaxWatts}W` : '';
    lines.push(`Out ${std}${watts}`);
  }
  if (port.passthrough) lines.push('Passthrough');
  lines.push(connected ? 'Cabled' : 'Open');
  return { title: name, lines };
}
